import { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Voice recorder using browser MediaRecorder API.
 * On stop, uploads audio blob to /api/voice/transcribe.
 * Calls onResult({ transcript, user_message, assistant_message, transactions }).
 */
export default function VoiceRecorder({ onResult, disabled }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    if (disabled || recording || uploading) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        await upload(blob, mime);
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      console.error(e);
      toast.error("Mic access denied. Allow microphone in your browser.");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const upload = async (blob, mime) => {
    if (blob.size < 1000) {
      toast.error("Recording too short");
      return;
    }
    setUploading(true);
    try {
      const ext = mime.includes("webm") ? "webm" : "mp4";
      const file = new File([blob], `voice.${ext}`, { type: mime });
      const fd = new FormData();
      fd.append("audio", file);
      const r = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onResult?.(r.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail?.slice(0, 80) || "Transcription failed. Add OpenAI credits to enable voice.");
    } finally {
      setUploading(false);
    }
  };

  if (uploading) {
    return (
      <button disabled className="w-10 h-10 rounded-full bg-kudi-green text-white flex items-center justify-center" data-testid="voice-uploading">
        <Loader2 className="w-4 h-4 animate-spin" />
      </button>
    );
  }

  if (recording) {
    const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
    const ss = String(elapsed % 60).padStart(2, "0");
    return (
      <button
        onClick={stop}
        className="flex items-center gap-2 bg-rose-600 text-white rounded-full px-3 h-10 animate-pulse"
        data-testid="voice-stop-btn"
      >
        <Square className="w-4 h-4 fill-white" />
        <span className="text-sm font-mono tabular-nums">{mm}:{ss}</span>
      </button>
    );
  }

  return (
    <button
      onClick={start}
      disabled={disabled}
      className="w-10 h-10 rounded-full bg-accent text-kudi-green flex items-center justify-center hover:bg-accent/70 transition disabled:opacity-40"
      title="Hold to speak (English/Pidgin)"
      data-testid="voice-record-btn"
    >
      <Mic className="w-5 h-5" />
    </button>
  );
}
